/** One person's formatting of one chart (redesign phase 2): line widths,
 *  points, and axis limits, saved for them on that chart
 *  (`/api/me/chart-format/{key}`). Formatting changes no data: the series'
 *  data arrays are passed through untouched, axis limits clip the view
 *  only, and the tooltip still shows every stored value. */
import type { EChartsOption } from 'echarts'
import type { Series } from '../api/types'

export type LineWidth = 1 | 1.5 | 2 | 3 | 4
export type PointSize = 2 | 3 | 4 | 6 | 8
export const LINE_WIDTHS: LineWidth[] = [1, 1.5, 2, 3, 4]
export const POINT_SIZES: PointSize[] = [2, 3, 4, 6, 8]

export interface SeriesFormat { width: LineWidth; points: boolean; point_size: PointSize }
export interface AxisFormat { min: number | null; max: number | null }

/** Series by parameter code, axes by axis_group (never by unit). */
export interface ChartFormat {
  series: Record<string, SeriesFormat>
  axes: Record<string, AxisFormat>
}

export const DEFAULT_SERIES: SeriesFormat = { width: 2, points: false, point_size: 4 }

interface Line { lineStyle?: Record<string, unknown>; [key: string]: unknown }
interface Axis { [key: string]: unknown }

/** The option with this person's formatting applied to the chart's own
 *  series (the first `series.length` in the option) and its Y axes, one per
 *  axis group in the order the chart made them. */
export function applyFormat(option: EChartsOption, format: ChartFormat | null,
                            series: Series[]): EChartsOption {
  if (!format) return option
  const lines = (option.series as Line[] | undefined) ?? []
  const groups = [...new Set(series.map((s) => s.axis_group))]
  const axes = Array.isArray(option.yAxis) ? (option.yAxis as Axis[]) : []

  const formattedLines = lines.map((line, i) => {
    const own = i < series.length ? format.series[series[i]!.parameter] : undefined
    if (!own) return line
    return {
      ...line,
      lineStyle: { ...line.lineStyle, width: own.width },
      showSymbol: own.points,
      symbol: 'circle',
      symbolSize: own.point_size,
    }
  })
  const formattedAxes = axes.map((axis, i) => {
    const own = groups[i] !== undefined ? format.axes[groups[i]!] : undefined
    if (!own) return axis
    return {
      ...axis,
      ...(own.min !== null ? { min: own.min } : {}),
      ...(own.max !== null ? { max: own.max } : {}),
    }
  })
  const changed = formattedLines.some((l, i) => l !== lines[i])
    || formattedAxes.some((a, i) => a !== axes[i])
  if (!changed) return option
  return {
    ...option,
    series: formattedLines as EChartsOption['series'],
    ...(axes.length ? { yAxis: formattedAxes as EChartsOption['yAxis'] } : {}),
  }
}

/** The explore chart's key: one per station. */
export function explorerChartKey(station: string): string {
  return `explore/${station}`
}

/** A gateway chart's key, from InstrumentPage's group key (a JSON triple of
 *  family, group and quantity, perhaps with ":axis" after it): each part a
 *  single path segment of at most 40 characters, "-" when empty, as the
 *  server's key check requires. The axis suffix stays on the quantity: a
 *  quantity split into two charts by axis group (metres on a piezometer,
 *  millimetres on a settlement gauge) is two charts, and sharing one saved
 *  format let each overwrite the other's. */
export function instrumentChartKey(station: string, groupKey: string): string {
  const end = groupKey.lastIndexOf(']') + 1
  const [family = '', group = '', quantity = ''] = JSON.parse(groupKey.slice(0, end)) as string[]
  const part = (value: string) => (value.replace(/\//g, '-').slice(0, 40) || '-')
  return `instrument/${station}/${[family, group, quantity + groupKey.slice(end)].map(part).join('/')}`
}
